import * as React from 'react'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import { inProgressMarker, stoppedMarker } from '@components/marker/business'

import useI18n from '@store/i18n/useI18n'
import { BsFillDiamondFill, FaMapMarker } from 'react-icons/all'

interface Props {
  isOpened: boolean
  setIsOpened: (open: boolean) => void
  source: 'home' | 'admin'
}

const ICON_SIZE = 30

const BusinessLegend = ({ isOpened, setIsOpened, source }: Props) => {
  const i18n = useI18n()
  const isHome = source === 'home'

  const displayLegendLine = (
    source: 'OSR' | 'SIROCCO',
    markerLabel: string,
    isHome: boolean,
    isStoppingPoint: boolean
  ) => (
    <LegendLine>
      <LegendIcon>
        {isHome ? (
          isStoppingPoint ? (
            stoppedMarker(ICON_SIZE, source)
          ) : (
            inProgressMarker(ICON_SIZE, source)
          )
        ) : isStoppingPoint ? (
          source === 'SIROCCO' ? (
            <FaMapMarker size={ICON_SIZE} color={Theme.colors.markerStopped} />
          ) : (
            <BsFillDiamondFill size={ICON_SIZE} color={Theme.colors.markerStopped} />
          )
        ) : source === 'SIROCCO' ? (
          <FaMapMarker size={ICON_SIZE} color={Theme.colors.markerInProgress} />
        ) : (
          <BsFillDiamondFill size={ICON_SIZE} color={Theme.colors.markerInProgress} />
        )}
      </LegendIcon>
      <LegendLibelle>{markerLabel}</LegendLibelle>
    </LegendLine>
  )

  return (
    <>
      <LegendeButtonContainer selected={isOpened} onClick={() => setIsOpened(!isOpened)}>
        <LegendButtonLibelle selected={isOpened}>{i18n.t('components.map.legend')}</LegendButtonLibelle>
      </LegendeButtonContainer>
      {isOpened && (
        <LegendContainer>
          {displayLegendLine('SIROCCO', i18n.t('components.map.sirocco_inprogress'), isHome, false)}
          {displayLegendLine('SIROCCO', i18n.t('components.map.sirocco_stopped'), isHome, true)}
          <Separator />
          {displayLegendLine('OSR', i18n.t('components.map.osr_inprogress'), isHome, false)}
          {displayLegendLine('OSR', i18n.t('components.map.osr_stopped'), isHome, true)}
          {isHome && (
            <>
              <Separator />
              <LegendLine>
                <LegendIcon legendType="clusters" />
                <LegendLibelle>{i18n.t('components.map.clusters')}</LegendLibelle>
              </LegendLine>
              <LegendLine>
                <LegendIcon legendType="orphans" />
                <LegendLibelle>{i18n.t('components.map.orphans')}</LegendLibelle>
              </LegendLine>
            </>
          )}
        </LegendContainer>
      )}
    </>
  )
}
export default BusinessLegend

const LegendeButtonContainer = styled('div')<{ selected: boolean }>`
  position: absolute;
  top: 15px;
  margin-left: 25px;

  cursor: pointer;
  border: 1px solid ${(props) => props.theme.colors.filterPrimarycolor};
  background-color: ${(props) =>
    props.selected ? props.theme.colors.filterPrimarycolor : props.theme.colors.filterSecondaryColor};
  box-sizing: border-box;
  border-radius: ${(props) => (props.selected ? '17px 17px 0 0' : '24.5px')};
  ${(props) => props.selected && 'border-bottom-width: 0;'}
  height: 35px;
  width: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 12px;
  box-shadow: 1px 1px 1px 1px #bbbe;
`
const LegendButtonLibelle = styled('div')<{ selected: boolean }>`
  ${(props) => props.theme.fonts.lonelyPictureLibelle}
  ${(props) => props.selected && `color: ${props.theme.colors.filterSecondaryColor};`}
`
const LegendContainer = styled('div')`
  position: absolute;
  top: 50px;
  margin-left: 25px;

  display: flex;
  padding: 10px;
  flex-direction: column;
  background: ${(props) => props.theme.colors.filterBackgroundColor};
  box-shadow: 1px 1px 1px 1px #bbbe;
`
const LegendLine = styled('div')`
  display: flex;
  align-items: center;
  height: 35px;
`
const LegendIcon = styled('div')<{ legendType?: 'orphans' | 'clusters' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px;
  height: 25px;
  width: 25px;
  margin-right: 5px;
  ${(props) =>
    props.legendType && `background-color: ${props.theme.colors.maplegend[props.legendType]}; border: 1px solid white;`}
`
const LegendLibelle = styled('div')`
  ${(props) => props.theme.fonts.checkboxLibelle};
  line-height: 15px;
`
const Separator = styled('div')`
  height: 2px;
  display: flex;
  background: linear-gradient(270deg, #1e868c 0%, #006167 100%);
  margin: 5px;
  border-radius: 5px;
`
